import AccountService from "../services/AccountService.js";
import AccountSync from "../services/AccountSync.js";
import { profileError } from "../services/accountErrors.js";
import { renderAvatarSVG, loadStoredAvatar } from "../utils/AvatarBuilder.js";

/**
 * Page /profil : identité (avatar, pseudo, email) puis la progression, qui est
 * rendue par `Scores.js` dans `.profile-progress` — l'ancienne vue « Ma
 * progression » du classement (`/leaderboard#mine`), déplacée ici pour que le
 * classement ne soit plus qu'un classement.
 *
 * Visible sans compte : la progression vit dans le localStorage, elle existe
 * donc aussi pour un anonyme. Seul le bloc d'identité change (invitation à
 * créer un compte au lieu des champs du compte).
 */
export default class Profile {
    constructor() {
        this.page = document.querySelector(".page.profile");
        if (!this.page) return;

        this.t = (k) => window.geoNerdApp.languageManager.t(k);
        this.avatarEl = this.page.querySelector(".profile-avatar");
        this.usernameEl = this.page.querySelector(".profile-username");
        this.anonymousBlock = this.page.querySelector(".profile-anonymous");
        this.accountBlock = this.page.querySelector(".profile-account");
        this.form = this.page.querySelector(".account-profile-form");

        this.bindFields();
        this.page.querySelector(".account-logout")?.addEventListener("click", () => this.handleLogout());
        this.form?.addEventListener("submit", (e) => this.handleProfileUpdate(e));

        this.render();
        window.geoNerdApp.accountReady?.then(() => this.render());
        document.addEventListener("geonerd:avatarchange", () => this.renderAvatar());
    }

    render() {
        const loggedIn = AccountSync.isLoggedIn();
        if (this.anonymousBlock) this.anonymousBlock.hidden = loggedIn;
        if (this.accountBlock) this.accountBlock.hidden = !loggedIn;

        this.renderAvatar();

        if (!loggedIn) return;

        if (this.usernameEl) {
            this.usernameEl.textContent = AccountSync.user.username || this.t("profile.noUsername");
        }
        const usernameInput = this.form?.querySelector(".account-username-input");
        if (usernameInput) usernameInput.value = AccountSync.user.username ?? "";
        const emailInput = this.form?.querySelector(".account-email-input");
        if (emailInput) emailInput.value = AccountSync.user.email ?? "";
        this.lockFields();
    }

    renderAvatar() {
        if (this.avatarEl) this.avatarEl.innerHTML = renderAvatarSVG(loadStoredAvatar(), 96);
    }

    /** Pseudo et email en lecture seule par défaut : un crayon par champ déverrouille. */
    bindFields() {
        this.page.querySelectorAll(".account-field-edit").forEach((btn) =>
            btn.addEventListener("click", () => {
                const input = btn.previousElementSibling;
                input.readOnly = false;
                input.focus();
            })
        );
        this.form?.querySelectorAll("input").forEach((input) =>
            input.addEventListener("input", () => {
                const btn = this.form.querySelector(".account-save-profile");
                if (btn) btn.disabled = false;
            })
        );
    }

    /** Reverrouille les champs et grise le bouton après un enregistrement (ou au rendu). */
    lockFields() {
        this.form?.querySelectorAll("input").forEach((input) => (input.readOnly = true));
        const btn = this.form?.querySelector(".account-save-profile");
        if (btn) btn.disabled = true;
    }

    async handleProfileUpdate(e) {
        e.preventDefault();
        const username = this.form.querySelector(".account-username-input").value.trim();
        const email = this.form.querySelector(".account-email-input").value.trim();

        this.setBusy(true);
        const res = await AccountService.patchProfile({ username: username || null, email });
        this.setBusy(false);

        if (res.ok && res.data) {
            AccountSync.user.username = res.data.username;
            AccountSync.user.email = res.data.email;
            this.render();
            this.showMessage(this.t("account.profileUpdated"), "success");
            return;
        }
        this.showMessage(profileError(res, this.t), "error");
    }

    async handleLogout() {
        await AccountService.logout();
        AccountSync.clear();
        window.location.reload();
    }

    setBusy(busy) {
        const btn = this.form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = busy;
        this.form.classList.toggle("is-busy", busy);
    }

    showMessage(msg, kind) {
        const el = this.form.querySelector(".account-message");
        if (!el) return;
        el.textContent = msg;
        el.className = `account-message ${kind}`;
    }
}
